import React, { useCallback } from "react";
import { Edit2 } from "lucide-react";
import qrCodeGenerator from "qrcode-generator";
import { QuestionPage, PageElement } from "../types/types";

// Tipagem para IDs de questões dissertativas
interface DissertativeQuestionId {
  id: string;
  globalIndex: number;
}

interface QuestionPagesProps {
  questionPages: QuestionPage[];
  A4_WIDTH: number;
  A4_HEIGHT: number;
  marginSuperior: number;
  marginInferior: number;
  marginEsquerda: number;
  marginDireita: number;
  borderVisible: boolean;
  numeroColunas: number;
  fontSizeQuestoes: number | "default";
  fontSizeAlternatives: number | "default";
  handleEditPage: (pageIndex: number) => void;
  handleInlineEdit: (
    pageIndex: number,
    elementIndex: number,
    newHtml: string
  ) => void;
  getElementType: (html: string) => string;
  dissettiveMode?: boolean;
  printMode?: boolean;
  testId?: string;
  diagramId?: string;
  answerSheetId?: string;
  registrationId?: string;
  studentId?: string;
  urltoken?: string;
  edit?: boolean;
}

export const QuestionPages: React.FC<QuestionPagesProps> = ({
  questionPages,
  A4_WIDTH,
  A4_HEIGHT,
  marginSuperior,
  marginInferior,
  marginEsquerda,
  marginDireita,
  borderVisible,
  numeroColunas,
  fontSizeQuestoes,
  fontSizeAlternatives,
  handleEditPage,
  handleInlineEdit,
  getElementType,
  printMode,
  testId,
  diagramId,
  answerSheetId,
  registrationId,
  studentId,
  urltoken,
  dissettiveMode,
  edit,
}) => {
  /**
   * Extrai os IDs de questão de elementos que tenham área de resposta.
   * Complexidade: O(n) – iteramos por cada elemento da página.
   */
  const extractDissertativeIds = useCallback(
    (elements: PageElement[]): DissertativeQuestionId[] => {
      const questionIds: DissertativeQuestionId[] = [];
      let counter = 1;

      elements.forEach((element) => {
        if (!element.element) return;

        // Busca por elementos com classes específicas de área de resposta
        if (
          element.element.includes('class="answer-area pentagon-answer-box"') ||
          (element.element.includes("answer-area") &&
            element.element.includes("pentagon-answer-box"))
        ) {
          // Extrai o ID da questão usando regex
          const match = element.element.match(/data-question-id="([^"]+)"/);
          if (match && match[1]) {
            questionIds.push({
              id: match[1],
              globalIndex: counter++, // Incrementa para criar um ID global único dentro da página
            });
          }
        }
      });
      return questionIds;
    },
    []
  );

  const generateQRCode = useCallback(
    (page: QuestionPage, pageIndex: number) => {
      // Se não estiver no modo dissertativo, não gera o QR Code
      if (!dissettiveMode) return null;

      // Tenta usar os IDs já presentes ou extrair do HTML
      let questionIds: DissertativeQuestionId[] =
        page.dissertativeData?.questionIds || [];
      if (questionIds.length === 0) {
        questionIds = extractDissertativeIds(page.elements);
      }

      // Calcula o offset acumulado dos IDs das páginas anteriores
      // Percorre as páginas anteriores e soma a quantidade de questões extraídas
      const previousQuestionsCount = questionPages
        .slice(0, pageIndex)
        .reduce((total, prevPage) => {
          const idsCount = prevPage.dissertativeData?.questionIds
            ? prevPage.dissertativeData.questionIds.length
            : extractDissertativeIds(prevPage.elements).length;
          return total + idsCount;
        }, 0);

      // Atualiza o globalIndex dos IDs da página atual, somando o offset
      const adjustedQuestionIds = questionIds.map((q) => ({
        ...q,
        globalIndex: q.globalIndex + previousQuestionsCount,
      }));

      // Cria a string de parâmetros, por exemplo: q3=12345
      const formattedQuestionParams = adjustedQuestionIds.length
        ? adjustedQuestionIds.map((q) => `q${q.globalIndex}=${q.id}`).join("&")
        : "";

      let qrCodeValue = "";

      // Monta a string do QR Code com ou sem urltoken
      if (urltoken) {
        const separator = urltoken.includes("?") ? "&" : "?";
        qrCodeValue = `${urltoken}${separator}page=${
          pageIndex + 1
        }&questionCount=${adjustedQuestionIds.length}`;
        if (formattedQuestionParams) {
          qrCodeValue += `&${formattedQuestionParams}`;
        }
        if (studentId && registrationId) {
          qrCodeValue += `&studentId=${studentId}&registrationId=${registrationId}`;
          if (testId) qrCodeValue += `&testId=${testId}`;
          if (answerSheetId) qrCodeValue += `&answerSheetId=${answerSheetId}`;
          if (diagramId) qrCodeValue += `&diagramId=${diagramId}`;
          if (edit) qrCodeValue += `&edit=true`;
        }
      } else {
        qrCodeValue = `page=${pageIndex + 1}&questionCount=${
          adjustedQuestionIds.length
        }`;
        if (formattedQuestionParams) {
          qrCodeValue += `&${formattedQuestionParams}`;
        }
        if (studentId && registrationId) {
          qrCodeValue = `studentId=${studentId}&registrationId=${registrationId}&testId=${
            testId || ""
          }&answerSheetId=${answerSheetId || ""}&diagramId=${
            diagramId || ""
          }&${qrCodeValue}`;
        }
      }

      // Geração do QR Code com a biblioteca
      const typeNumber = 0;
      const errorCorrectionLevel = "M";
      const qr = qrCodeGenerator(typeNumber, errorCorrectionLevel);
      qr.addData(qrCodeValue);
      qr.make();

      // Ajusta o tamanho das células para uma imagem nítida
      const cellSize = 6;
      const margin = 4;
      const moduleCount = qr.getModuleCount();
      const totalSize = moduleCount * cellSize + margin * 2;
      const viewBoxAttr = `viewBox="0 0 ${totalSize} ${totalSize}"`;

      // Gera o SVG do QR Code com o viewBox ajustado
      const rawQrSvg = qr.createSvgTag(cellSize, margin);
      const qrSvg = rawQrSvg.replace(
        "<svg",
        `<svg style="width:90px; height:90px;" ${viewBoxAttr}`
      );

      // Verifica se já existe um separador na página
      const hasSeparator = page.elements.some(
        (element) =>
          element.element &&
          (element.element.includes('data-element-type="separator"') ||
            element.element.includes('id="separator"'))
      );

      return (
        <>
          {/* QR Code posicionado no topo */}
          <div
            className="dynamic-qr-code"
            style={{
              position: "absolute",
              top: `${marginSuperior}px`,
              right: `${marginDireita}px`,
              width: "90px",
              height: "90px",
              zIndex: 900,
              backgroundColor: "white",
              padding: "4px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              borderRadius: "4px",
            }}
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />

          {/* Se não houver separador, adiciona um na página */}
          {!hasSeparator && (
            <div
              className="qr-separator"
              style={{
                position: "absolute",
                top: `${marginSuperior + 100}px`,
                left: `${marginEsquerda}px`,
                width: `calc(100% - ${marginEsquerda + marginDireita}px)`,
                height: "2px",
                backgroundColor: "#000",
                zIndex: 10,
              }}
              data-element-type="separator"
              id="separator"
            />
          )}
        </>
      );
    },
    [
      extractDissertativeIds,
      questionPages,
      testId,
      diagramId,
      answerSheetId,
      registrationId,
      studentId,
      urltoken,
      edit,
      marginSuperior,
      marginDireita,
      marginEsquerda,
      dissettiveMode, // Adicionando dissettiveMode às dependências
    ]
  );

  return (
    <>
      {questionPages.map((page, pageIndex) => {

        return (
          <div key={`page-container-${pageIndex}`} className="page-container">
            <div
              key={`question-page-${pageIndex}`}
              className={`a4-page mx-auto print-visible ${
                borderVisible ? "border border-dark" : ""
              }`}
              style={{
                width: A4_WIDTH,
                height: A4_HEIGHT,
                position: "relative",
                background: "#fff",
                padding: `${marginSuperior}px ${marginDireita}px ${marginInferior}px ${marginEsquerda}px`,
                borderStyle: borderVisible ? "double" : "none",
                borderWidth: borderVisible ? "2px" : "0",
                marginBottom: "20px",
              }}
              data-page-index={pageIndex}
            >
              {/* Gera o QR Code para a página apenas se estiver em modo dissertativo */}
              {generateQRCode(page, pageIndex)}

              {printMode && (
                <>
                  <button
                    className="btn btn-sm btn-primary no-print"
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      zIndex: 950,
                    }}
                    onClick={() => handleEditPage(pageIndex)}
                  >
                    <Edit2 size={14} /> Editar Página
                  </button>

                  <div
                    className="column-indicator no-print"
                    style={{
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "4px",
                      display: "flex",
                    }}
                  >
                    {Array.from({ length: page.columns || numeroColunas }).map(
                      (_, i) => (
                        <div
                          key={i}
                          style={{
                            flex: 1,
                            height: "100%",
                            background: i % 2 === 0 ? "#0d6efd" : "#6c757d",
                            opacity: 0.5,
                          }}
                        />
                      )
                    )}
                  </div>
                </>
              )}

              {page.elements.map((element, elementIndex) => {


                const elementType = getElementType(element.element);
                const isAlternative = elementType === "alternative";
                const isEditable = !(
                  elementType === "qr_code" || elementType === "separator"
                );

                return (
                  <div
                    key={`question-element-${pageIndex}-${elementIndex}`}
                    className="question-element"
                    data-element-type={elementType}
                    style={{
                      position: "absolute",
                      left: element.x,
                      top: element.y,
                      width: element.width,
                      height: element.height,
                      cursor: "default",
                      overflow: "visible",
                      pointerEvents: "auto",
                    }}
                    contentEditable={isEditable}
                  >
                    <div
                      className="element-content"
                      style={{
                        width: "100%",
                        height: "100%",
                        overflow: "visible",
                        fontSize: `${
                          isAlternative
                            ? typeof fontSizeAlternatives === "number"
                              ? fontSizeAlternatives
                              : 13
                            : typeof fontSizeQuestoes === "number"
                            ? fontSizeQuestoes
                            : 13
                        }px`,
                        userSelect: "text",
                        padding: "2px",
                      }}
                      dangerouslySetInnerHTML={{ __html: element.element }}
                    />
                  </div>
                );
              })}

              <div
                className="page-number text-center no-print"
                style={{
                  position: "absolute",
                  bottom: "8px",
                  left: 0,
                  right: 0,
                  fontSize: "0.8rem",
                  color: "#666",
                  userSelect: "none",
                }}
              >
                Página {pageIndex + 1} de {questionPages.length}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};
